import type React from 'react';
import { useEffect } from 'react';

import { Box, ToggleButton } from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { BASE_RENT, PETS } from '../../../constants/index';
import { FEE_PAYMENT_TIME, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import FeePaymentTime from '../../../components/Widgets/FeePaymentTime';
import PetsYesAndNoExpansion from './Pets/PetsYesAndNoExpansion';
import PetsInsuranceYesAndNo from './Pets/PetsInsuranceYesAndNo';
import PetsCurrencyInput from './Pets/PetsCurrencyInput';
import PetsBaseRentButtonGroup from './Pets/PetsBaseRentButtonGroup';

const BOUNDS = PETS.BOUNDS.V5;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  [PETS.FIELD_NAME.IS_ENABLED]: false,
  [PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE]: null,
  [PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH]: '',
  [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT]: '',
  [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: null,
  [PETS.FIELD_NAME.IS_MONTHLY]: null,
  [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: null,
  [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_START_MOVE_IN_DATE]: null,
  [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_UPON_SIGNING]: null,
  [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE_AND_MONTHLY]: null,
};

export default function Pets_v5({ section, setSectionValues }: Props) {
  function isDepositAmountValid(amount: string): boolean {
    const maximumDepositAmount = section.getFieldValue(BASE_RENT.FIELD_NAME.AMOUNT)
      ? Math.floor(section.getFieldValue(BASE_RENT.FIELD_NAME.AMOUNT) / 2)
      : BOUNDS.MAX_DEPOSIT_AMOUNT;

    if (Number(amount.replace(/,/g, '')) > maximumDepositAmount) {
      section
        .getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)
        .setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(maximumDepositAmount));
      return false;
    }
    return true;
  }

  useEffect(() => {
    section.setCustomFieldValidator(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT, isDepositAmountValid);
  }, []);

  function onChargesChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;
    const { fieldName } = event.currentTarget.dataset;

    setSectionValues({ [fieldName]: value });
  }

  function onNoPetsClick() {
    setSectionValues(INITIAL_STATE);
  }

  function onYesPetsClick() {
    setSectionValues({ [PETS.FIELD_NAME.IS_ENABLED]: true });
  }

  function onNoInsuranceClick() {
    setSectionValues({ [PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE]: false });
  }

  function onYesInsuranceClick() {
    setSectionValues({ [PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE]: true });
  }

  function onNoAdditionalFeeClick(): void {
    setSectionValues({
      [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: true,
      [PETS.FIELD_NAME.IS_MONTHLY]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: false,
      [PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH]: '',
      [PETS.FIELD_NAME.NON_REFUNDABLE_FEE]: '',
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT]: '',
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_START_MOVE_IN_DATE]: false,
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_UPON_SIGNING]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE_AND_MONTHLY]: false,
    });
  }

  function onMonthlyClick(): void {
    setSectionValues({
      [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: false,
      [PETS.FIELD_NAME.IS_MONTHLY]: true,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: false,
      [PETS.FIELD_NAME.NON_REFUNDABLE_FEE]: '',
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT]: '',
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_START_MOVE_IN_DATE]: false,
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_UPON_SIGNING]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE_AND_MONTHLY]: false,
    });
  }

  function onRefundableDepositClick(): void {
    setSectionValues({
      [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: false,
      [PETS.FIELD_NAME.IS_MONTHLY]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: true,
      [PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH]: '',
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_START_MOVE_IN_DATE]: false,
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_UPON_SIGNING]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE_AND_MONTHLY]: false,
    });
  }

  function onRefundableDepositAndMonthlyClick(): void {
    setSectionValues({
      [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: false,
      [PETS.FIELD_NAME.IS_MONTHLY]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: false,
      [PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH]: '',
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_START_MOVE_IN_DATE]: false,
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_UPON_SIGNING]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE_AND_MONTHLY]: true,
    });
  }

  function onRefundablePaymentFeeDueClick(event: React.ChangeEvent<HTMLInputElement>): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_UPON_SIGNING]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_SIGN,
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_START_MOVE_IN_DATE]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_LEASE_START,
    });
  }

  function renderPetRent(): React.ReactNode {
    return (
      <PetsCurrencyInput
        label="Pet rent"
        value={section.getFieldValue(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH)}
        onChange={onChargesChange}
        onBlur={onChargesChange}
        field={section.getField(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH)}
        dataFieldName={PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH}
        perMonth
        formHelp="Amount generally should not exceed $30 per pet"
      />
    );
  }

  function renderRefundableDeposit(): React.ReactNode {
    return (
      <>
        <PetsCurrencyInput
          label="Refundable pet deposit"
          value={section.getFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)}
          onChange={onChargesChange}
          onBlur={onChargesChange}
          field={section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)}
          dataFieldName={PETS.FIELD_NAME.REFUNDABLE_DEPOSIT}
          perMonth={false}
          formHelp="A refundable pet deposit is treated as a security deposit"
        />
        <Box css={{ marginTop: 'layout.tight' }}>
          <FeePaymentTime
            label="When is the refundable pet deposit due?"
            section={section}
            firstChecked={section.getFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_UPON_SIGNING)}
            secondChecked={section.getFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT_START_MOVE_IN_DATE)}
            onChange={onRefundablePaymentFeeDueClick}
          />
        </Box>
      </>
    );
  }

  const isNoAdditionalCharge = section.getFieldValue(PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE);
  const isMonthly = section.getFieldValue(PETS.FIELD_NAME.IS_MONTHLY);
  const isRefundable = section.getFieldValue(PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE);
  const isRefundableAndMonthly = section.getFieldValue(PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE_AND_MONTHLY);

  return (
    <PetsYesAndNoExpansion
      yesHandler={onYesPetsClick}
      noHandler={onNoPetsClick}
      yesActive={section.getFieldValue(PETS.FIELD_NAME.IS_ENABLED)}
    >
      <Box css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }}>
        <PetsInsuranceYesAndNo
          dataTest={PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE}
          noHandler={onNoInsuranceClick}
          yesActive={section.getFieldValue(PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE)}
          yesHandler={onYesInsuranceClick}
        />
      </Box>
      <PetsBaseRentButtonGroup>
        <ToggleButton
          selected={isNoAdditionalCharge}
          data-field-name="no-additional-charges-button"
          onClick={onNoAdditionalFeeClick}
        >
          No additional charge
        </ToggleButton>
        <ToggleButton selected={isMonthly} data-field-name="monthly-pet-rent-button" onClick={onMonthlyClick}>
          Yes, monthly pet rent
        </ToggleButton>
        <ToggleButton
          selected={isRefundable}
          data-field-name="refundable-deposit-button"
          onClick={onRefundableDepositClick}
        >
          Yes, refundable deposit
        </ToggleButton>
        <ToggleButton
          selected={isRefundableAndMonthly}
          data-field-name="refundable-deposit-and-monthy-pet-rent-button"
          onClick={onRefundableDepositAndMonthlyClick}
        >
          Yes, deposit and pet rent
        </ToggleButton>
      </PetsBaseRentButtonGroup>
      <Box css={{ marginTop: 'layout.tight' }} />
      {isMonthly && renderPetRent()}
      {isRefundable && renderRefundableDeposit()}
      {isRefundableAndMonthly && (
        <>
          {renderPetRent()}
          <Box css={{ marginTop: 'layout.tight' }} />
          {renderRefundableDeposit()}
        </>
      )}
    </PetsYesAndNoExpansion>
  );
}

Pets_v5.description = 'Pets v4 with the omission of non-refundable fees';
